/**
 * External dependencies
 */
import { isEqual } from 'lodash';
import { createAlternative, createGoal, EMPTY_ARRAY } from '@nab/utils';
import type {
	Alternative,
	AlternativeId,
	AnyAction,
	Experiment,
	Maybe,
} from '@nab/types';

/**
 * Internal dependencies
 */
import { INIT_STATE } from '../config';
import type { State as FullState } from '../types';
import type { ExperimentAction as Action } from '../actions/experiment';

type State = FullState[ 'experiments' ];

export function experiments(
	state = INIT_STATE.experiments,
	action: AnyAction
): State {
	return actualReducer( state, action as Action ) ?? state;
}

function actualReducer( state: State, action: Action ): State {
	switch ( action.type ) {
		case 'RECEIVE_EXPERIMENT': {
			const newExperiment = {
				...action.experiment,
				alternatives: maybeAddDefaultAlternatives(
					action.experiment.alternatives
				),
				goals: action.experiment.goals ?? [ createGoal() ],
				segments: action.experiment.segments || EMPTY_ARRAY,
			};
			if ( isEqual( state[ action.key ], newExperiment ) ) {
				return state;
			}
			return {
				...state,
				[ action.key ]: newExperiment,
			};
		}

		case 'CHANGE_LAST_APPLIED_ALTERNATIVE':
			const experiment: Maybe< Experiment > =
				state[ action.experimentId ];
			if ( ! experiment ) {
				return state;
			}

			const newExperiment = {
				...experiment,
				alternatives: experiment.alternatives.map(
					isLastApplied( action.alternativeId )
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				) as any as Experiment[ 'alternatives' ],
			};

			if ( isEqual( state[ action.experimentId ], newExperiment ) ) {
				return state;
			}

			return {
				...state,
				[ action.experimentId ]: newExperiment,
			};
	}
}

// =======
// HELPERS
// =======

const isLastApplied = ( alternativeId: AlternativeId ) =>
	function < T extends Alternative >( alternative: T ): T {
		const newAlternative = {
			...alternative,
			isLastApplied: alternative.id === alternativeId,
		};
		return isEqual( alternative, newAlternative )
			? alternative
			: newAlternative;
	};

function maybeAddDefaultAlternatives< T extends Experiment[ 'alternatives' ] >(
	alternatives: T
): T {
	const defaultOriginal = createAlternative( 'control' );
	const defaultFirstAlt = createAlternative();

	if ( ! alternatives?.length ) {
		return [ defaultOriginal, defaultFirstAlt ] as unknown as T;
	}

	if ( alternatives[ 0 ].id !== 'control' ) {
		return [ defaultOriginal, ...alternatives ] as unknown as T;
	}

	if ( 1 === alternatives.length ) {
		return [ alternatives[ 0 ], defaultFirstAlt ] as unknown as T;
	}

	return alternatives;
}
